import { Button } from "GameObjects/UI/Button";
import * as Constants from "Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";
import Settings from "Data/Settings";
import {ResultsScreenLayout} from "Layouts/ResultsScreenLayout";
import { AbstractGameState } from "./AbstractGameState";
import { Intro } from "./Intro";
import { MainMenu } from "States/MainMenu";
import { FlyingGame } from "States/FlyingGame";
import GameState, { IRuntimeDuck } from "Data/GameState";
import { LayoutSprite } from "../GameObjects/UI/LayoutSprite";
import { Background } from "../GameObjects/UI/Background";
import { Sine, TimelineMax, TweenLite, TweenMax } from "gsap";
import BadgePopup from "../Popups/BadgePopup";
import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { Application } from "Application";

export class ResultsScreen extends AbstractGameState<typeof ResultsScreenLayout> implements ReturnType<typeof ResultsScreenLayout> {

    public static readonly NAME = "ResultsScreen";
    public readonly layout = ResultsScreenLayout;

    public readonly bg: LayoutSprite;
    public readonly banner: LayoutSprite;
    public readonly characters: LayoutSprite[];
    public readonly shadow: LayoutSprite[];
    public readonly fiveducks: LayoutSprite;
    public readonly sevenducks: LayoutSprite;
    public readonly nineducks: LayoutSprite;
    public readonly buttonfive: LayoutButton;
    public readonly buttonseven: LayoutButton;
    public readonly buttonnine: LayoutButton;

    protected jackieScale: number = 1;
    protected mattScale: number = 1;
    protected inezScale: number = 1;
    protected readonly inez: LayoutSprite;
    protected readonly jackie: LayoutSprite;
    protected readonly matt: LayoutSprite;


    protected hoverTimeline?: TimelineMax;
    protected bgContainer: Background;
    protected ducksCount!: number;
    protected bgm = HLF.Application.instance.audio.getAudioTrack("flyingloop", HLF.AudioCategory.BGM) ?? HLF.Application.instance.audio.createAudioTrack("flyingloop", HLF.AudioCategory.BGM);

    private idleTimeline?: TimelineMax;

    public constructor() {
        super();
        this.bgContainer = new Background();
        this.bg = new LayoutSprite();
        this.banner = new LayoutSprite();
        this.characters = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.inez = this.characters[0];
        this.jackie = this.characters[1];
        this.matt = this.characters[2];
        this.fiveducks = new LayoutSprite();
        this.sevenducks = new LayoutSprite();
        this.nineducks = new LayoutSprite();
        this.shadow = [
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
            new LayoutSprite(),
        ];
        this.buttonfive = new LayoutButton(this.onPlayAgainFiveDucksButtonClicked.bind(this), [VO.REWARD_PLAY_AGAIN_HOVER, VO.DUCKNUM_5], SFX.UI_CLICK);
        this.buttonseven = new LayoutButton(this.onPlayAgainSevenDucksButtonClicked.bind(this), [VO.REWARD_PLAY_AGAIN_HOVER, VO.DUCKNUM_7], SFX.UI_CLICK);
        this.buttonnine = new LayoutButton(this.onPlayAgainNineDucksButtonClicked.bind(this), [VO.REWARD_PLAY_AGAIN_HOVER, VO.DUCKNUM_9], SFX.UI_CLICK);
    }

    public init(pSize: PIXI.Point, pData: {name: string, ducks?: IRuntimeDuck[]}) {
        // draw order
        this.addChild(this.bgContainer);
        this.bgContainer.addChild(this.bg);
        this.addChild(this.banner);
        this.addChild(this.fiveducks);
        this.addChild(this.sevenducks);
        this.addChild(this.nineducks);
        this.addChild(...this.shadow);
        this.addChild(...this.characters);
        this.addChild(this.buttonfive);
        this.addChild(this.buttonseven);
        this.addChild(this.buttonnine);
        super.init(pSize, pData);

        this.jackieScale = this.jackie.scale.y;
        this.inezScale = this.inez.scale.y;
        this.mattScale = this.matt.scale.y;

        this.homeButton.visible = false;
        this.characterContainer.visible = false;
        this.settingsButton.visible = false;
        this.helpButton.visible = false;
        this.buttonfive.visible = false;
        this.buttonseven.visible = false;
        this.buttonnine.visible = false;

        if (pData.ducks) {
            this.ducksCount = pData.ducks.length;
        }
        else {
            this.ducksCount = 5;
        }

        this.fiveducks.visible = this.ducksCount === 5;
        this.sevenducks.visible = this.ducksCount === 7;
        this.nineducks.visible = this.ducksCount === 9;

        this.hoverTimeline = new TimelineMax();
        this.hoverTimeline.repeat(-1);
        this.hoverTimeline.staggerFromTo(this.characters, 1, {
            pixi: {
                anchorY: 0.455,
            },
        },
            {
                pixi: {
                    anchorY: 0.505,
                },
                ease: Sine.easeInOut,
            }, 0.25)
            .staggerFromTo(this.characters, 0.95, {
                pixi: {
                    anchorY: 0.505,
                },
            },
                {
                    pixi: {
                        anchorY: 0.455,
                    },
                    ease: Sine.easeInOut
                }, 0.05);

        this.hoverTimeline.paused(!Settings.moreAnimation);

        if (Settings.moreAnimation === true) {
            TweenLite.from(this.banner, 0.5, {pixi: {scale: 0}});
        }
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [
            this.buttonfive,
            this.buttonseven,
            this.buttonnine,
            this.settingsButton,
            this.homeButton,
        ]);

        if (Settings.closedCaptionsEnabled === false && Settings.voiceoverVolume === 0) {
            PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(BadgePopup.NAME, this.onBadgePopupClosed.bind(this), true, true, {
                ducksCount: this.ducksCount,
            }));
        }
        else {
            const vosToPlay = [VO.REWARD_INTRO_1];

            if (Settings.audioDescriptionEnabled) {
                vosToPlay.push(VO.REWARD_AUDIO_DESCRIPTION);
            }
            vosToPlay.push(VO.REWARD_INTRO_2);

            Application.instance.voiceover.playVO(vosToPlay, {mode: HLF.PlayMode.Append, callback: ()=>{
                if (GameState.freePlay === false) {
                    // todo: don't show badge popup if no badges to award
                    PubSub.publishSync(HLF.SHOW_POPUP, new HLF.PopupToken(BadgePopup.NAME, this.onBadgePopupClosed.bind(this), true, true, {
                        ducksCount: this.ducksCount,
                    }));
                }
                else {
                    this.onBadgePopupClosed();
                }
            }});
        }

        HLF.Application.instance.audio.play(SFX.FANFARE, 1, false, HLF.AudioCategory.SFX);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
    }

    public destroy(pOptions: Parameters<typeof HLF.State.prototype.destroy>[0]) {
        super.destroy(pOptions);
        this.hoverTimeline?.kill();
        this.idleTimeline?.kill();
    }

    public gotoNextState(name: string, data?: object) {
        this.stopIdleTimer();
        super.gotoNextState(name, data);
    }

    protected playHelpVO() {
        this.idleTimeline?.restart();
        HLF.Application.instance.voiceover.playVO(VO.RESULTS_HELP, ()=>{
            this.idleTimeline?.restart();
        });
    }

    protected onVoiceoverStarted(pTopic: typeof HLF.VOICEOVER_STARTED, pData: string): void {
        // no need to call super.onVoiceoverStarted here
        if (pData.indexOf("jackie") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {scale: 1.25}});
            TweenLite.to(this.matt, 0.5, {pixi: {scale: this.mattScale}});
            TweenLite.to(this.inez, 0.5, {pixi: {scale: this.inezScale}});
        }
        else if (pData.indexOf("matt") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {scale: this.jackieScale}});
            TweenLite.to(this.matt, 0.5, {pixi: {scale: 1.25}});
            TweenLite.to(this.inez, 0.5, {pixi: {scale: this.inezScale}});
        }
        else if (pData.indexOf("inez") === 0) {
            TweenLite.to(this.jackie, 0.5, {pixi: {scale: this.jackieScale}});
            TweenLite.to(this.matt, 0.5, {pixi: {scale: this.mattScale}});
            TweenLite.to(this.inez, 0.5, {pixi: {scale: 1.25}});
        }
    }
    protected onPlayAgainFiveDucksButtonClicked() {
        this.playAgain(5);
    }

    protected onPlayAgainSevenDucksButtonClicked() {
        this.playAgain(7);
    }

    protected onPlayAgainNineDucksButtonClicked() {
        this.playAgain(9);
    }

    protected playAgain(pHowManyDucks: number) {
        if (pHowManyDucks > this.ducksCount) {
            if (Settings.difficulty !== 0 && Settings.difficulty !== (3 / 3)) {
                Settings.difficulty += 1/3;
            }
        }
        GameState.flyingTimeElapsed = 0;
        // reset badges
        GameState.dodgedAllObstacles = true;
        GameState.perfectPonds = true;
        GameState.swaptastic = true;
        FlyingGame.increaseDifficultyNextRound = false;
        FlyingGame.decreaseDifficultyNextRound = false;

        Application.instance.analytics.event("level_up", FlyingGame.NAME, pHowManyDucks);
        this.gotoNextState(FlyingGame.NAME, {ducks: GameState.GetDucks(pHowManyDucks)});
    }

    protected onBadgePopupClosed() {
        this.buttonfive.visible = this.ducksCount <= 7;
        this.buttonseven.visible = true;
        this.buttonnine.visible = this.ducksCount >= 7;

        if (this.ducksCount < 7) {
            this.buttonfive.position.x += this.buttonfive.width / 2;
            this.buttonseven.position.x += this.buttonseven.width / 2;
        }
        else if (this.ducksCount > 7) {
            this.buttonseven.position.x -= this.buttonseven.width / 2;
            this.buttonnine.position.x -= this.buttonnine.width / 2;
        }

        this.settingsButton.visible = true;
        this.helpButton.visible = true;
        TweenMax.staggerFrom([this.buttonfive, this.buttonseven, this.buttonnine], 0.5, {pixi: {scale: 0}}, 0.1);
        let voToPlay: string;
        if (this.ducksCount < 7) {
            voToPlay = VO.REWARD_IDLE;
        }
        else if (this.ducksCount === 7) {
            voToPlay = VO.REWARD_IDLE_2;
        }
        else {
            voToPlay = VO.REWARD_IDLE_3;
        }
        this.restartIdleTimer();
        Application.instance.voiceover.playVO(voToPlay, ()=>{
            this.restartIdleTimer();
        });
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        super.onSettingsChanged(pTopic, pSettings);
        this.hoverTimeline?.paused(!pSettings.moreAnimation);
    }

    protected onSettingsButtonPressed(): void {
        this.idleTimeline?.kill();
        super.onSettingsButtonPressed();
    }

    protected onSettingsPopupClosed() {
        super.onSettingsPopupClosed();
        this.restartIdleTimer();
    }

    private restartIdleTimer() {
        if (this.idleTimeline === undefined) {
            console.log("start idle timer");
            this.idleTimeline = new TimelineMax({repeat: -1});
            this.idleTimeline.call(()=>{
                console.log("idle!");
                let voToPlay: string;
                if (this.ducksCount < 7) {
                    voToPlay = VO.REWARD_IDLE;
                }
                else if (this.ducksCount === 7) {
                    voToPlay = VO.REWARD_IDLE_2;
                }
                else {
                    voToPlay = VO.REWARD_IDLE_3;
                }
                Application.instance.voiceover.playVO(voToPlay, ()=>{
                    this.restartIdleTimer();
                });
            }, undefined, undefined, 10);
        }
        else {
            this.idleTimeline?.kill();
            this.idleTimeline?.restart();
            console.log("restart idle timer");
        }
    }

    private stopIdleTimer() {
        if (this.idleTimeline !== undefined) {
            console.log("stop idle timer");
            this.idleTimeline.kill();
        }
    }
}
